/**
 * Download menu for the Wholesale Workspace — mirrors
 * components/audit/DownloadMenu.tsx ~line 146-275 verbatim:
 *   - navy primary button (8/16 padding, 14px/500, opacity 0.5 disabled)
 *   - dropdown menu (260px min-width, silver border, md radius, hover bg)
 *   - menu item style with primary label + smaller secondary subtitle
 *   - 1px silver divider between items
 *   - click-outside + Escape close
 *   - error displayed below the menu in semantic.danger
 *
 * Two options:
 *   - Current View: just the active tab (Cleared / Not Cleared / Unmatched)
 *   - Full Report:  all 3 tabs in one workbook
 *
 * Disabled until a run has been committed in this session — the page
 * passes `runId` only after the user clicks Commit Run, so before that
 * point the button stays greyed out.
 */
import React, { useEffect, useRef, useState } from "react"
import { ChevronDown, Download } from "lucide-react"

import {
  WholesaleTab,
  exportWholesaleRunFull,
  exportWholesaleRunTab,
} from "../../api/wholesale"
import { THEME } from "../../config/theme"

type DownloadVariant = "current" | "full"

// Per-tab filename labels — mirrors _TAB_FILENAME_LABEL in
// backend/routers/wholesale.py so the FE `download` hint matches the
// backend Content-Disposition.
const TAB_FILENAME_LABEL: Record<WholesaleTab, string> = {
  cleared:           "Cleared",
  not_cleared:       "Not_Cleared",
  unmatched_adesa:   "Unmatched_Adesa",
  excluded:          "Excluded",
}

interface Props {
  runId: number | null
  activeTab: WholesaleTab
  runStartedAt: string | null
  // Fill the row (mobile: the button stacks full-width with the others).
  fullWidth?: boolean
}

export default function DownloadMenu({
  runId,
  activeTab,
  runStartedAt,
  fullWidth = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const disabled = downloading || runId === null

  // Close on click outside.
  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open])

  const handleDownload = async (variant: DownloadVariant) => {
    if (runId === null) return
    setOpen(false)
    setDownloading(true)
    setError(null)
    try {
      const blob = variant === "full"
        ? await exportWholesaleRunFull(runId)
        : await exportWholesaleRunTab(runId, activeTab)
      const filename = formatFilename(variant, activeTab, runStartedAt)
      triggerBrowserDownload(blob, filename)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download")
    } finally {
      setDownloading(false)
    }
  }

  const buttonStyle: React.CSSProperties = {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    borderRadius: THEME.borderRadius.md,
    border: "none",
    backgroundColor: THEME.colors.navy,
    color: THEME.colors.white,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    userSelect: "none",
    transition: "opacity 0.15s",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: THEME.spacing.xs,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
    ...(fullWidth && { width: "100%" }),
  }

  const menuStyle: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    minWidth: 260,
    backgroundColor: THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    zIndex: 100,
    overflow: "hidden",
  }

  const menuItemStyle = (itemDisabled: boolean): React.CSSProperties => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    border: "none",
    backgroundColor: "transparent",
    cursor: itemDisabled ? "not-allowed" : "pointer",
    fontSize: 13,
    color: itemDisabled
      ? THEME.colors.neutral.light.textSecondary
      : THEME.colors.neutral.light.textPrimary,
    fontFamily: THEME.fonts.primary,
    transition: "background-color 0.1s",
  })

  const menuItemLabelStyle: React.CSSProperties = {
    fontWeight: 500,
    marginBottom: 2,
  }

  const menuItemSubtitleStyle: React.CSSProperties = {
    fontSize: 12,
    color: THEME.colors.neutral.light.textSecondary,
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-block",
        ...(fullWidth && { width: "100%" }),
      }}
    >
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        style={buttonStyle}
      >
        <Download size={14} strokeWidth={2} />
        {downloading ? "Preparing…" : "Download"}
        <ChevronDown size={14} strokeWidth={2} />
      </button>

      {open && (
        <div style={menuStyle} role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => handleDownload("current")}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor =
                THEME.colors.neutral.light.background
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"
            }}
            style={menuItemStyle(false)}
          >
            <div style={menuItemLabelStyle}>Current View</div>
            <div style={menuItemSubtitleStyle}>
              {`Just the ${displayLabel(activeTab)} tab`}
            </div>
          </button>
          <div style={{ height: 1, backgroundColor: THEME.colors.silver }} />
          <button
            type="button"
            role="menuitem"
            onClick={() => handleDownload("full")}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor =
                THEME.colors.neutral.light.background
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"
            }}
            style={menuItemStyle(false)}
          >
            <div style={menuItemLabelStyle}>Full Report</div>
            <div style={menuItemSubtitleStyle}>All 3 sheets in one workbook</div>
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            fontSize: 12,
            color: THEME.colors.semantic.danger,
            whiteSpace: "nowrap",
          }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

function displayLabel(tab: WholesaleTab): string {
  if (tab === "cleared") return "Cleared"
  if (tab === "not_cleared") return "Not Cleared"
  return "Unmatched Adesa"
}

function formatFilename(
  variant: DownloadVariant,
  tab: WholesaleTab,
  runStartedAt: string | null,
): string {
  const pad = (n: number) => n.toString().padStart(2, "0")
  const date = runStartedAt ? new Date(runStartedAt) : new Date()
  const datePart = `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`
  if (variant === "full") {
    return `INVI_Dolan_Full_WS_Report_${datePart}.xlsx`
  }
  return `INVI_Dolan_${TAB_FILENAME_LABEL[tab]}_${datePart}.xlsx`
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
